'use client';

import { useEffect, useRef } from 'react';

/**
 * Hand-rolled Canvas 2D starfield (workspace PLAN.md §5.1 canvas tier) — the
 * ONE sanctioned animation-JS island on (marketing); everything else on the
 * route stays CSS-only. Approved with the Bay Reveal (wiki/VISUAL-DESIGN.md).
 *
 * Perf contract (verified during mockup review — an unpaused loop can hang a
 * slow compositor): ≤137 pooled stars in 3 parallax layers, delta-time rAF,
 * DPR cap 2, pause on visibilitychange AND when scrolled off-viewport;
 * prefers-reduced-motion renders a single static frame.
 */
export default function Starfield() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    interface Star {
      x: number;
      y: number;
      r: number;
      a: number;
      tw: number;
      tws: number;
      s: number;
    }
    interface Shot {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
    }

    interface Layer {
      n: number;
      speed: number;
      r: readonly [number, number];
      a: readonly [number, number];
    }
    const LAYERS: readonly Layer[] = [
      { n: 70, speed: 0.0045, r: [0.4, 0.9], a: [0.25, 0.5] },
      { n: 45, speed: 0.009, r: [0.7, 1.3], a: [0.4, 0.7] },
      { n: 22, speed: 0.016, r: [1.0, 1.8], a: [0.6, 0.95] },
    ];

    let W = 0;
    let H = 0;
    let stars: Star[] = [];
    let shooting: Shot | null = null;
    let nextShot = 4000;
    let last = performance.now();
    let rafId = 0;
    let running = false;
    let visible = true;

    // The parent .space-view is full-viewport; CSS transforms scale it during
    // the Bay Reveal, so the canvas itself always renders at viewport size.
    const starColor = getComputedStyle(cv).getPropertyValue('--color-star').trim() || '#fff';

    function seedStars() {
      stars = [];
      for (const L of LAYERS) {
        for (let i = 0; i < L.n; i += 1) {
          stars.push({
            x: Math.random() * W,
            y: Math.random() * H,
            r: L.r[0] + Math.random() * (L.r[1] - L.r[0]),
            a: L.a[0] + Math.random() * (L.a[1] - L.a[0]),
            tw: Math.random() * Math.PI * 2,
            tws: 0.0006 + Math.random() * 0.0012,
            s: L.speed,
          });
        }
      }
    }

    function resize() {
      const prevW = W;
      const prevH = H;
      W = window.innerWidth;
      H = window.innerHeight;
      if (!cv || !ctx) return;
      cv.width = W * DPR;
      cv.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      if (stars.length === 0) {
        seedStars();
      } else if (prevW > 0 && prevH > 0) {
        // Re-project instead of re-seeding: a mobile URL-bar collapse must not
        // visibly reshuffle the sky mid-reveal.
        for (const st of stars) {
          st.x = (st.x / prevW) * W;
          st.y = (st.y / prevH) * H;
        }
      }
      if (reducedMotion) frame(performance.now()); // repaint the static frame
    }

    function frame(now: number) {
      if (!ctx) return;
      const dt = Math.min(now - last, 50);
      last = now;
      ctx.clearRect(0, 0, W, H);
      for (const st of stars) {
        st.x -= st.s * dt;
        st.tw += st.tws * dt;
        if (st.x < -4) {
          st.x = W + 4;
          st.y = Math.random() * H;
        }
        ctx.globalAlpha = st.a * (0.72 + 0.28 * Math.sin(st.tw));
        ctx.fillStyle = starColor;
        ctx.beginPath();
        ctx.arc(st.x, st.y, st.r, 0, 7);
        ctx.fill();
      }
      nextShot -= dt;
      if (!shooting && nextShot <= 0) {
        shooting = {
          x: W * (0.2 + Math.random() * 0.6),
          y: H * 0.08 + Math.random() * H * 0.25,
          vx: -0.55,
          vy: 0.22,
          life: 900,
        };
        nextShot = 7000 + Math.random() * 9000;
      }
      if (shooting) {
        shooting.life -= dt;
        shooting.x += shooting.vx * dt;
        shooting.y += shooting.vy * dt;
        const t = Math.max(shooting.life / 900, 0);
        const grad = ctx.createLinearGradient(
          shooting.x,
          shooting.y,
          shooting.x + 70,
          shooting.y - 28,
        );
        grad.addColorStop(0, `rgba(242, 246, 255, ${0.85 * t})`);
        grad.addColorStop(1, 'rgba(242, 246, 255, 0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.4;
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(shooting.x, shooting.y);
        ctx.lineTo(shooting.x + 70, shooting.y - 28);
        ctx.stroke();
        if (shooting.life <= 0) shooting = null;
      }
      ctx.globalAlpha = 1;
      // rafId guards against duplicate loops: a stop()+start() pair inside one
      // frame interval must not leave two live rAF chains running forever.
      if (running) rafId = requestAnimationFrame(frame);
    }

    function start() {
      if (running || reducedMotion || document.hidden || !visible) return;
      running = true;
      last = performance.now();
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      cancelAnimationFrame(rafId);
    }

    resize();
    window.addEventListener('resize', resize);

    let io: IntersectionObserver | null = null;
    const onVisibility = () => (document.hidden ? stop() : start());

    if (reducedMotion) {
      frame(performance.now()); // single static frame
    } else {
      io = new IntersectionObserver(([entry]) => {
        visible = entry?.isIntersecting ?? true;
        if (visible) start();
        else stop();
      });
      io.observe(cv);
      document.addEventListener('visibilitychange', onVisibility);
      start();
    }

    return () => {
      stop();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      io?.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="starfield-canvas" aria-hidden="true" />;
}
